import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, CheckCircle2, AlertCircle } from "lucide-react";
import { Reveal, SectionHead } from "./shared";
import { PHONE_DISPLAY, PHONE_TEL } from "./Nav";

const SERVICE_OPTIONS = ["Home Remodels", "Custom Decks", "Room Additions", "Fencing", "Roofing", "Tree Services", "Handyman Services", "Other"];

const inputCls =
  "w-full bg-ink-elev border border-line focus:border-safety text-bone placeholder:text-steel/50 px-4 py-3.5 text-base transition-colors duration-200 clip-notch-sm";

export default function QuoteForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "Home Remodels",
    address: "",
    message: "",
  });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    setStatus("loading");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="py-20 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_50%,rgba(204,85,0,0.08),transparent_60%)]" aria-hidden="true" />
      <div className="relative max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <SectionHead
            eyebrow="Free quote"
            title="Tell us what you're building."
            sub="Fill this out and we'll call you back to schedule a walkthrough. Prefer to talk it through right now? So would we."
          />
          <Reveal delay={0.2}>
            <a
              href={PHONE_TEL}
              data-testid="quote-call-btn"
              className="inline-flex items-center gap-3 mt-10 bg-safety hover:bg-safety-hover text-ink font-bold text-lg px-8 py-4 clip-notch-sm transition-all duration-200 hover:-translate-y-1"
            >
              <Phone className="w-5 h-5" strokeWidth={2.5} />
              Call {PHONE_DISPLAY}
            </a>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <div className="bg-ink-card border border-line clip-notch p-6 sm:p-10">
              <AnimatePresence mode="wait">
                {status === "success" ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid="quote-form-success"
                    className="text-center py-14"
                  >
                    <CheckCircle2 className="w-14 h-14 text-safety mx-auto" strokeWidth={1.75} />
                    <h3 className="font-cond font-bold uppercase text-3xl text-bone mt-6">Request received.</h3>
                    <p className="text-steel mt-3 max-w-md mx-auto">
                      Thanks — your request is in. We'll reach out shortly to schedule your free walkthrough.
                    </p>
                    <button
                      onClick={() => { setStatus("idle"); setForm({ name: "", email: "", phone: "", service: "Home Remodels", address: "", message: "" }); }}
                      data-testid="quote-form-reset"
                      className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-safety font-bold border border-line hover:border-safety px-6 py-3 transition-colors"
                    >
                      Send another request
                    </button>
                  </motion.div>
                ) : (
                  <motion.form key="form" onSubmit={submit} initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="quote-form" className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="qf-name" className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel block mb-2">Name *</label>
                        <input id="qf-name" required value={form.name} onChange={set("name")} data-testid="quote-input-name" placeholder="Your name" className={inputCls} />
                      </div>
                      <div>
                        <label htmlFor="qf-phone" className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel block mb-2">Phone *</label>
                        <input id="qf-phone" required type="tel" value={form.phone} onChange={set("phone")} data-testid="quote-input-phone" placeholder="(555) 000-0000" className={inputCls} />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="qf-email" className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel block mb-2">Email *</label>
                      <input id="qf-email" required type="email" value={form.email} onChange={set("email")} data-testid="quote-input-email" placeholder="you@email.com" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="qf-service" className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel block mb-2">What are we working on? *</label>
                      <div className="flex flex-wrap gap-2">
                        {SERVICE_OPTIONS.map((s) => (
                          <button
                            type="button"
                            key={s}
                            onClick={() => setForm({ ...form, service: s })}
                            data-testid={`quote-service-tag-${s.toLowerCase().replace(/ /g, "-")}`}
                            className={`font-cond font-semibold uppercase tracking-wide text-sm px-4 py-2 border transition-all duration-200 ${
                              form.service === s ? "border-safety bg-safety text-ink" : "border-line text-steel hover:border-steel hover:text-bone"
                            }`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label htmlFor="qf-address" className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel block mb-2">Property address</label>
                      <input id="qf-address" value={form.address} onChange={set("address")} data-testid="quote-input-address" placeholder="Street, city" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="qf-message" className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel block mb-2">Project details</label>
                      <textarea id="qf-message" rows={4} value={form.message} onChange={set("message")} data-testid="quote-input-message" placeholder="What does the finished project look like?" className={`${inputCls} resize-none`} />
                    </div>

                    {status === "error" && (
                      <p data-testid="quote-form-error" className="flex items-center gap-2 text-safety text-sm font-semibold">
                        <AlertCircle className="w-4 h-4" /> Something went wrong — please call us instead.
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={status === "loading"}
                      data-testid="quote-form-submit"
                      className="w-full bg-safety hover:bg-safety-hover disabled:opacity-60 text-ink font-bold text-lg py-4 clip-notch-sm transition-all duration-200 hover:-translate-y-0.5"
                    >
                      {status === "loading" ? "Sending…" : "Request My Free Quote"}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
